import { z } from "zod";

// Profile validation schemas
export const profileUpdateSchema = z.object({
  username: z.string()
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must be less than 30 characters")
    .regex(/^[a-zA-Z0-9_-]+$/, "Username can only contain letters, numbers, underscores, and hyphens"),
  full_name: z.string()
    .trim()
    .max(100, "Name must be less than 100 characters")
    .optional()
    .or(z.literal("")),
  bio: z.string()
    .trim()
    .max(500, "Bio must be less than 500 characters")
    .optional()
    .or(z.literal("")),
  location: z.string()
    .trim()
    .max(100, "Location must be less than 100 characters")
    .optional()
    .or(z.literal("")),
  avatar_url: z.string().url("Invalid URL").optional().or(z.literal("")),
});

// Phone number validation schema
export const phoneSchema = z.string()
  .optional()
  .refine((val) => !val || /^\+?[\d\s()-]{10,}$/.test(val), {
    message: "Please enter a valid phone number"
  });

// Comment validation schema
export const commentSchema = z.object({
  content: z.string()
    .trim()
    .min(1, "Comment cannot be empty")
    .max(1000, "Comment must be less than 1000 characters"),
});

// Herd message validation schema
export const herdMessageSchema = z.object({
  content: z.string()
    .trim()
    .min(1, "Message cannot be empty")
    .max(5000, "Message must be less than 5000 characters"),
});

// Buddy request validation schema
export const buddyRequestSchema = z.object({
  message: z.string()
    .trim()
    .max(500, "Message must be less than 500 characters")
    .optional(),
});

// Herd creation validation schema
export const herdRequestSchema = z.object({
  name: z.string()
    .trim()
    .min(3, "Herd name must be at least 3 characters")
    .max(100, "Herd name must be less than 100 characters"),
  description: z.string()
    .trim()
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description must be less than 1000 characters"),
  category: z.string()
    .min(1, "Please select a category"),
  neighborhood: z.string()
    .max(100, "Neighborhood must be less than 100 characters")
    .optional(),
});

// Password validation schema
export const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character");

// Event host request validation schema
export const eventHostRequestSchema = z.object({
  title: z.string()
    .trim()
    .min(5, "Title must be at least 5 characters")
    .max(200, "Title must be less than 200 characters"),
  description: z.string()
    .trim()
    .min(10, "Description must be at least 10 characters")
    .max(2000, "Description must be less than 2000 characters"),
  category: z.string()
    .min(1, "Please select a category"),
  location: z.string()
    .trim()
    .min(5, "Location must be at least 5 characters")
    .max(200, "Location must be less than 200 characters"),
  date: z.date({
    required_error: "Please select a date",
  }).refine((date) => {
    // Allow events at least 1 hour in the future to prevent edge cases
    const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
    return date > oneHourFromNow;
  }, {
    message: "Event must be at least 1 hour in the future",
  }),
  duration_min: z.number()
    .min(15, "Duration must be at least 15 minutes")
    .max(480, "Duration must be less than 8 hours"),
  max_attendees: z.number()
    .min(2, "Must allow at least 2 attendees")
    .max(1000, "Maximum 1000 attendees")
    .optional(),
});

// Admin event validation schema
export const adminEventSchema = z.object({
  title: z.string()
    .trim()
    .min(5, "Title must be at least 5 characters")
    .max(200, "Title must be less than 200 characters"),
  description: z.string()
    .trim()
    .max(600, "About this event must be 600 characters or less")
    .optional(),
  category: z.string()
    .min(1, "Please select a category"),
  location: z.string()
    .trim()
    .min(3, "Location must be at least 3 characters")
    .max(200, "Location must be less than 200 characters"),
  date: z.string().datetime("Invalid date format"),
  duration_min: z.number()
    .int("Duration must be a whole number")
    .min(15, "Duration must be at least 15 minutes")
    .max(480, "Duration must be less than 8 hours"),
  host_name: z.string()
    .trim()
    .min(1, "Host name is required")
    .max(200, "Host name must be less than 200 characters"),
  source_url: z.string()
    .trim()
    .max(500, "Source URL must be less than 500 characters")
    .default(""),
  source_name: z.string()
    .trim()
    .max(100, "Source name must be less than 100 characters")
    .default("Manual Entry"),
  level: z.string().max(50, "Level must be less than 50 characters").optional(),
  is_active: z.boolean(),
  needs_external_rsvp: z.boolean(),
  host_logo_url: z.string().url("Invalid URL").optional().or(z.literal("")),
});

// Admin event approval schema (for approving user requests)
export const adminEventApprovalSchema = z.object({
  title: z.string()
    .trim()
    .min(5, "Title must be at least 5 characters")
    .max(200, "Title must be less than 200 characters"),
  description: z.string()
    .trim()
    .max(5000, "Description must be less than 5000 characters")
    .optional(),
  category: z.string()
    .min(1, "Category is required"),
  location: z.string()
    .trim()
    .min(3, "Location must be at least 3 characters")
    .max(200, "Location must be less than 200 characters"),
  date: z.string().min(1, "Date is required"),
  duration_min: z.number()
    .int("Duration must be a whole number")
    .min(15, "Duration must be at least 15 minutes")
    .max(480, "Duration must be less than 8 hours")
    .optional(),
  max_attendees: z.number()
    .int("Max attendees must be a whole number")
    .min(2, "Must allow at least 2 attendees")
    .max(1000, "Maximum 1000 attendees")
    .optional(),
  host_name: z.string()
    .trim()
    .max(200, "Host name must be less than 200 characters"),
  host_logo_url: z.string().url("Invalid URL").optional().or(z.literal("")),
});

// Admin herd approval schema
export const adminHerdApprovalSchema = z.object({
  name: z.string()
    .trim()
    .min(3, "Herd name must be at least 3 characters")
    .max(100, "Herd name must be less than 100 characters"),
  description: z.string()
    .trim()
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description must be less than 1000 characters"),
  category: z.string()
    .min(1, "Category is required"),
  neighborhood: z.string()
    .max(100, "Neighborhood must be less than 100 characters")
    .optional(),
  image_url: z.string().url("Invalid URL").optional().or(z.literal("")),
});
